<template>
  <v-app-bar
    id="navbar"
    flat
    color="darknavyblue"
    class="text-white"
    height="60"
    :extended="!!routeMeta.isTabsList"
    extension-height="55"
  >
    <v-badge
      dot
      :model-value="!!totalUnreadReleaseNotes"
      color="error"
    >
      <v-btn
        v-if="onBack"
        id="navbar--back"
        key="v-btn-1"
        data-test="navbar-back"
        class="text-white"
        icon
        size="large"
        @click="goBack"
      >
        <v-icon icon="mdi-arrow-left" />
      </v-btn>
      <v-btn
        v-else-if="!hideSidebar"
        id="navbar--sidebar-toggle"
        key="v-btn-2"
        data-test="navbar-sidebar-toggle"
        class="text-white ml-1"
        icon
        @click.prevent="toggleSidebar"
      >
        <v-icon icon="mdi-menu" />
      </v-btn>
    </v-badge>
    <v-btn
      v-if="showHome"
      data-test="navbar-home"
      theme="dark"
      icon
      @click="goHome"
    >
      <v-icon icon="mdi-home" />
    </v-btn>
    <v-toolbar-title class="!text-2xl mx-2">
      <span v-text="title" />
      <span v-if="isBetaEnv"> (Beta)</span>
      <PolicyChip
        v-if="showChip"
        class="ml-2"
        v-bind="chipProps"
      />
    </v-toolbar-title>
    <v-toolbar-items>
      <div class="flex gap-4">
        <LocationAvatars v-if="showLocationUsers" :location="location" />
        <ProgramSwitcher v-if="showSwitcher" />
      </div>
    </v-toolbar-items>
    <template v-if="routeMeta.isTabsList" #extension>
      <NavbarHeader />
    </template>
  </v-app-bar>
</template>

<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { computed, defineComponent } from 'vue'
import { useRoute } from 'vue-router'

import LocationAvatars from '@/components/common/LocationAvatars/LocationAvatars.vue'
import NavbarHeader from '@/components/common/NavbarHeader/NavbarHeader.vue'
import ProgramSwitcher from '@/components/common/ProgramSwitcher/ProgramSwitcher.vue'
import PolicyChip from '@/components/global/PolicyChip/PolicyChip.vue'
import { usePermission } from '@/composables/permission'
import { useApiStore } from '@/stores/api'
import { useAppContextStore } from '@/stores/appContext'
import { useApplicationStore } from '@/stores/application'
import { useAuthenticationStore } from '@/stores/authentication'
import { useCmsStore } from '@/stores/cms'
import { useDocumentStore } from '@/stores/document'
import { useFormApplicationStore } from '@/stores/form/application'
import { useGlobalStore } from '@/stores/global'
import { useNotificationStore } from '@/stores/notification'

import { FORM_NAVIGATION_WARNING } from '@/constants'
import routeNames from '@/router/routeNames'

import type { AgencyPermission } from '@/composables/permission'
import type { GroupDetail } from '@policyfly/protobuf/patrick'
import type { ApplicationCategory } from '@policyfly/types/application'

export default defineComponent({
  name: 'Navbar',

  components: {
    LocationAvatars,
    PolicyChip,
    ProgramSwitcher,
    NavbarHeader,
  },

  emits: {
    toggle: (): true => true,
  },

  props: {
    hideSidebar: { type: Boolean, default: false },
    onBack: { type: String, default: undefined },
  },

  setup (props) {
    const { checkPermission } = usePermission<AgencyPermission>()
    const notificationStore = useNotificationStore()
    const appContextStore = useAppContextStore()

    const applicationStore = useApplicationStore()
    const PNI = computed(() => applicationStore.PNI)

    const apiStore = useApiStore()
    const showSwitcher = computed(() => {
      return !props.hideSidebar && apiStore.isLoggedIn
    })
    const showChip = computed(() => applicationStore.ready && routeMeta.value.applicationLayout)
    const showLocationUsers = computed(() => applicationStore.ready && routeMeta.value.applicationLayout)
    const location = computed<GroupDetail['location'] | undefined>(() => {
      if (!routeMeta.value.applicationLayout) return undefined
      return {
        oneofKind: 'application',
        application: {
          id: String(appContextStore.applicationId),
        },
      }
    })

    const route = useRoute()
    const routeMeta = computed(() => route.meta ?? {})

    const chipProps = computed(() => {
      return {
        type: routeMeta.value.policyView ? 'policy' : 'application' as 'policy' | 'application',
        kind: appContextStore.kind,
        category: (appContextStore.isCancellation ? 'CAN' : appContextStore.category ?? 'NEW') as ApplicationCategory,
      }
    })

    return {
      checkPermission,
      notificationStore,

      PNI,

      showSwitcher,
      showChip,
      showLocationUsers,

      chipProps,
      location,
      routeMeta,
    }
  },

  computed: {
    ...mapState(useCmsStore, {
      totalUnreadReleaseNotes: 'countSinceLastRead',
    }),
    ...mapState(useAuthenticationStore, {
      currentProgram: 'currentProgram',
    }),
    ...mapState(useGlobalStore, {
      isBetaEnv: 'isBetaEnv',
    }),
    ...mapStores(
      useDocumentStore,
      useFormApplicationStore,
    ),
    showHome (): boolean {
      return this.$route.name !== routeNames.HOME &&
        this.$route.name !== routeNames.PRIVACY &&
        this.$route.name !== routeNames.TERMS
    },
    title (): string {
      const { matched, name } = this.$route
      if (matched.some((r) => r.meta.applicationLayout)) return this.PNI
      if (matched.some((r) => r.meta.accountLayout)) {
        return 'My Account'
      }
      switch (name) {
        case routeNames.HOME:
        case routeNames.TASKS:
          return 'Home'
        case routeNames.RELEASE_NOTES:
          return 'Program Alerts'
        case routeNames.RATING:
          return 'Rating'
        case routeNames.REPORTING:
          return 'Reporting'
        case routeNames.USER_USERSLIST:
          return 'Internal Users'
        case routeNames.USER_CREATE:
        case routeNames.USER_VIEW:
          return 'User Management'
        case routeNames.DOCUMENT_DOCUMENTSLIST:
        case routeNames.DOCUMENT_NEW:
          return 'Document Manager'
        case routeNames.DOCUMENT_REVISIONSLIST:
        case routeNames.DOCUMENT_REVISION_NEW:
        case routeNames.DOCUMENT_REVISION:
          return (this.documentStore.loadedDocument || {}).verbose_name || 'Revisions'
        case routeNames.FORGOT_PASSWORD_RESET_PASSWORD:
          return 'Reset Password'
        case routeNames.FORGOT_PASSWORD:
          return 'Forgot Password'
        case routeNames.AGENCY_LIST:
          return 'Agencies'
        case routeNames.MY_AGENCIES:
          return 'My Agencies'
        case routeNames.AGENCY_DETAIL:
          // assuming that someone cannot view my agency & all agencies too
          if (this.checkPermission('myAgencyView')) {
            return 'My Agency'
          } else {
            return 'Agencies'
          }
        default:
          return ''
      }
    },
  },

  methods: {
    goBack (): void {
      if (!this.onBack) return
      if (this.onBack === '-1') this.$router.back()
      else this.$router.push({ name: this.onBack })
    },
    async goHome (): Promise<void> {
      if (this.$route.meta?.form && !await this.notificationStore.confirm({ title: FORM_NAVIGATION_WARNING })) return
      this.$router.push({ name: routeNames.HOME })
    },
    toggleSidebar (): void {
      this.$emit('toggle')
    },
  },
})
</script>

<style lang="sass" scoped>
#navbar
  :deep(.v-toolbar__extension)
    padding: 0
  // @vuetify-override
  :deep(.v-badge__badge)
    bottom: calc(100% - 12px) !important
</style>
